import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const DOT = { green: "bg-emerald-400", red: "bg-red-500", gray: "bg-zinc-500" };
const LABEL = { green: "Reachable", red: "Failed", gray: "Unknown" };

export function MagnusHealth() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data: h } = useQuery({ queryKey: ["magnus-health"], queryFn: async () => (await api.get("/admin/magnus/health")).data });
  const { data: ip } = useQuery({ queryKey: ["magnus-ip"], queryFn: async () => (await api.get("/admin/magnus/egress-ip")).data, staleTime: 3600000 });
  const st = h?.status || "gray";
  const check = async () => {
    setBusy(true);
    try { qc.setQueryData(["magnus-health"], (await api.post("/admin/magnus/health-check")).data); qc.invalidateQueries({ queryKey: ["sip-history"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="magnus-health" className="mt-4 rounded-lg border border-white/10 p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-semibold">
          <span data-testid="magnus-health-dot" data-status={st} className={`h-2.5 w-2.5 rounded-full ${DOT[st]}`} />
          SIP health: <span data-testid="magnus-health-label">{LABEL[st]}</span>
        </span>
        <button data-testid="magnus-health-check" onClick={check} disabled={busy}
          className="rounded-full border border-white/15 px-3 py-1 font-semibold hover:border-primary disabled:opacity-50">
          {busy ? "Checking…" : "Check now"}
        </button>
      </div>
      <p data-testid="magnus-health-detail" className="mt-1.5 text-muted-foreground">
        {h?.detail || "—"}{h?.latency_ms != null ? ` · ${h.latency_ms} ms` : ""}
      </p>
      <p data-testid="magnus-health-last" className="text-muted-foreground">
        Last check: {h?.checked_at ? new Date(h.checked_at).toLocaleString() : "never"}
        {h?.consecutive_failures > 0 ? ` · ${h.consecutive_failures} failure(s) in a row` : ""}
      </p>
      <p className="mt-1.5 text-muted-foreground">
        Whitelist only this app IP (UDP 5060): <span data-testid="magnus-egress-ip" className="font-mono-metric text-foreground">{ip?.ip || "detecting…"}</span>
      </p>
    </div>
  );
}

const CELL = { green: "bg-emerald-400", red: "bg-red-500", mixed: "bg-amber-400", unknown: "bg-zinc-700" };

export function SipHistory() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", host: "", port: 5060 });
  const { data } = useQuery({ queryKey: ["sip-history"], queryFn: async () => (await api.get("/admin/magnus/history")).data, refetchInterval: 300000 });
  const add = async () => {
    try { await api.post("/admin/magnus/targets", { ...form, port: Number(form.port) || 5060 }); setForm({ name: "", host: "", port: 5060 }); qc.invalidateQueries({ queryKey: ["sip-history"] }); toast.success("Carrier added — it's checked on the next run"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/magnus/targets/${id}`); qc.invalidateQueries({ queryKey: ["sip-history"] }); } catch (e) { toast.error(apiError(e)); }
  };
  const inp = "w-full min-w-0 rounded-md border border-white/15 bg-transparent px-2 py-1.5 outline-none focus:border-primary";
  return (
    <div data-testid="sip-history" className="mt-3 space-y-3 rounded-lg border border-white/10 p-3 text-xs">
      <p className="font-semibold">7-day SIP reachability (hourly)</p>
      <p data-testid="sip-history-note" className="text-amber-300/90">Reachability = the server answered a SIP check. It does not prove call quality (audio, connect rate, delay).</p>
      {(data?.targets || []).map((t) => (
        <div key={t.id} data-testid={`sip-history-${t.id}`} className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{t.name} <span className="font-mono-metric text-muted-foreground">{t.host}:{t.port}</span></span>
            {t.id !== "magnus" && <button data-testid={`sip-target-del-${t.id}`} onClick={() => del(t.id)} className="text-red-400 hover:underline">Remove</button>}
          </div>
          <div className="flex h-4 gap-px" title="Oldest → newest">
            {t.buckets.map((b, i) => <span key={i} className={`flex-1 rounded-[1px] ${CELL[b]}`} />)}
          </div>
          <p className="text-muted-foreground">
            Reachable <span data-testid={`sip-pct-${t.id}`} className="text-foreground">{t.reachable_pct == null ? "—" : `${t.reachable_pct}%`}</span> of {t.checks_green + t.checks_red} checks
            {" · "}<span data-testid={`sip-unknown-${t.id}`}>Unknown: {t.unknown_hours} of 168 h</span> (no data, not counted)
          </p>
        </div>
      ))}
      <div className="flex flex-wrap gap-3 text-muted-foreground">
        {[["green", "Reachable"], ["red", "Failed"], ["mixed", "Mixed"], ["unknown", "Unknown"]].map(([k, l]) => (
          <span key={k} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-sm ${CELL[k]}`} />{l}</span>
        ))}
      </div>
      <div className="grid grid-cols-[1fr_1fr_70px] gap-2 border-t border-white/10 pt-3">
        <input data-testid="sip-target-name" className={inp} placeholder="Carrier name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="sip-target-host" className={inp} placeholder="SIP host / IP" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
        <input data-testid="sip-target-port" className={inp} type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
      </div>
      <button data-testid="sip-target-add" onClick={add} disabled={!form.name || !form.host}
        className="rounded-full border border-white/15 px-3 py-1.5 font-semibold hover:border-primary disabled:opacity-50">Add carrier to monitor</button>
      <p className="text-muted-foreground">Carriers that only whitelist your softswitch IP will show Failed from here.</p>
    </div>
  );
}

export function MagnusAlerts() {
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["magnus-alerts"], queryFn: async () => (await api.get("/admin/magnus/alerts")).data });
  const v = f || (data && { ...data, emails: (data.emails || []).join(", ") });
  if (!v) return null;
  const set = (k, val) => setF({ ...v, [k]: val });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...v, emails: v.emails.split(",").map((e) => e.trim()).filter(Boolean), threshold: Number(v.threshold) || 3 };
      qc.setQueryData(["magnus-alerts"], (await api.post("/admin/magnus/alerts", body)).data); setF(null);
      toast.success("Alert settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const inp = "w-full rounded-md border border-white/15 bg-transparent px-2 py-1.5 outline-none focus:border-primary";
  return (
    <div data-testid="magnus-alerts" className="mt-3 space-y-2 rounded-lg border border-white/10 p-3 text-xs">
      <p className="font-semibold">Alerts</p>
      <p className="text-muted-foreground">Email after repeated failures and again on recovery. Checked automatically every 15 min.</p>
      <input data-testid="magnus-alert-emails" className={inp} value={v.emails} onChange={(e) => set("emails", e.target.value)} placeholder="alerts@iyeneh.com, you@iyeneh.com" />
      <label className="flex items-center gap-2">Alert after
        <input data-testid="magnus-alert-threshold" type="number" min="1" max="20" className={`${inp} w-16`} value={v.threshold} onChange={(e) => set("threshold", e.target.value)} /> failures in a row
      </label>
      <label className="flex items-center gap-2">
        <input data-testid="magnus-alert-sms-enabled" type="checkbox" checked={!!v.sms_enabled} onChange={(e) => set("sms_enabled", e.target.checked)} /> Also send SMS (optional)
      </label>
      {v.sms_enabled && <input data-testid="magnus-alert-sms-to" className={inp} value={v.sms_to} onChange={(e) => set("sms_to", e.target.value)} placeholder="+1XXXXXXXXXX" />}
      <button data-testid="magnus-alert-save" onClick={save} disabled={busy}
        className="rounded-full bg-primary px-3 py-1.5 font-semibold text-primary-foreground disabled:opacity-50">Save alerts</button>
    </div>
  );
}

export function MagnusCallerId() {
  const qc = useQueryClient();
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data: c, isError } = useQuery({ queryKey: ["magnus-cid"], queryFn: async () => (await api.get("/admin/magnus/caller-id")).data });
  const apply = async (enable) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/magnus/caller-id", { enable, reviewed_ani: enable ? c.fixed_ani : "" });
      qc.setQueryData(["magnus-cid"], data); setReviewed(false);
      toast.success(enable ? "Members now call with their own verified number" : "Fixed caller ID restored");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (isError) return <p className="mt-3 text-xs text-red-400" data-testid="magnus-cid-error">Couldn't read the softphone caller-ID setting.</p>;
  if (!c) return null;
  return (
    <div data-testid="magnus-cid" className="mt-3 rounded-lg border border-white/10 p-3 text-xs">
      <p className="font-semibold">Caller ID</p>
      {c.fixed_ani ? (
        <>
          <p className="mt-1 text-muted-foreground">
            The trunk currently forces every call to show <span data-testid="magnus-cid-fixed" className="font-mono-metric text-foreground">{c.fixed_ani}</span>.
            Switch to member caller ID so each member shows their own verified iYeneh number (checked server-side).
          </p>
          <label className="mt-2 flex items-center gap-2">
            <input data-testid="magnus-cid-reviewed" type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
            I reviewed the fixed caller ID {c.fixed_ani}
          </label>
          <button data-testid="magnus-cid-enable" onClick={() => apply(true)} disabled={!reviewed || busy}
            className="mt-2 rounded-full bg-primary px-3 py-1.5 font-semibold text-primary-foreground disabled:opacity-50">
            Use member caller ID
          </button>
        </>
      ) : (
        <>
          <p data-testid="magnus-cid-member-on" className="mt-1 text-emerald-400">Member caller ID is on — each call shows the member's own verified number.</p>
          {c.previous_ani && (
            <button data-testid="magnus-cid-restore" onClick={() => apply(false)} disabled={busy}
              className="mt-2 rounded-full border border-white/15 px-3 py-1.5 font-semibold disabled:opacity-50">
              Restore fixed {c.previous_ani}
            </button>
          )}
        </>
      )}
    </div>
  );
}
